'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Type, Square, Circle, Image as ImageIcon, Trash2, Download,
  ChevronUp, ChevronDown, AlignLeft, AlignCenter, AlignRight,
  Plus, Minus, RotateCcw, Copy, Layers
} from 'lucide-react';
import type { FlyerState, CanvasElement } from './FlyerBuilderContent';

interface Template {
  id: string;
  name: string;
  platform: string;
  width: number;
  height: number;
  bgColor: string;
  elements: CanvasElement[];
}

interface CanvasEditorProps {
  flyerState: FlyerState;
  setFlyerState: React.Dispatch<React.SetStateAction<FlyerState>>;
  templates: Template[];
  onTemplateSelect: (id: string) => void;
  onCaptureFlyer?: (base64: string) => void;
}

const SCALE = 0.45;

export default function CanvasEditor({ flyerState, setFlyerState, templates, onTemplateSelect, onCaptureFlyer }: CanvasEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedEl = flyerState.elements.find((el) => el.id === selectedId) ?? null;

  const updateElement = useCallback((id: string, updates: Partial<CanvasElement>) => {
    setFlyerState((prev) => ({
      ...prev,
      elements: prev.elements.map((el) => (el.id === id ? { ...el, ...updates } : el)),
    }));
  }, [setFlyerState]);

  const addTextElement = () => {
    const newEl: CanvasElement = {
      id: `el-${Date.now()}`,
      type: 'text',
      x: 60,
      y: 60,
      width: 400,
      height: 60,
      content: 'Double-click to edit',
      fontSize: 32,
      fontWeight: '600',
      color: '#0F0A1E',
      zIndex: flyerState.elements.length + 1,
      textAlign: 'left',
    };
    setFlyerState((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedId(newEl.id);
  };

  const addShapeElement = (shapeType: 'rect' | 'circle') => {
    const newEl: CanvasElement = {
      id: `el-${Date.now()}`,
      type: 'shape',
      x: 100,
      y: 100,
      width: 200,
      height: 80,
      shapeType,
      bgColor: '#6C47FF',
      color: '#FFFFFF',
      content: '',
      fontSize: 18,
      fontWeight: '600',
      zIndex: flyerState.elements.length + 1,
      textAlign: 'center',
    };
    setFlyerState((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedId(newEl.id);
  };

  const deleteElement = (id: string) => {
    setFlyerState((prev) => ({ ...prev, elements: prev.elements.filter((el) => el.id !== id) }));
    setSelectedId(null);
  };

  const duplicateElement = (id: string) => {
    const el = flyerState.elements.find((e) => e.id === id);
    if (!el) return;
    const newEl = { ...el, id: `el-${Date.now()}`, x: el.x + 20, y: el.y + 20, zIndex: flyerState.elements.length + 1 };
    setFlyerState((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedId(newEl.id);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      const newEl: CanvasElement = {
        id: `el-${Date.now()}`,
        type: 'image',
        x: 50,
        y: 50,
        width: 300,
        height: 200,
        imageUrl: url,
        imageAlt: file.name,
        zIndex: flyerState.elements.length + 1,
      };
      setFlyerState((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
      setSelectedId(newEl.id);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedId(id);
    const el = flyerState.elements.find((el) => el.id === id);
    if (!el) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragging(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging || !selectedId || !canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = (e.clientX - canvasRect.left - dragOffset.x) / SCALE;
    const newY = (e.clientY - canvasRect.top - dragOffset.y) / SCALE;
    updateElement(selectedId, { x: Math.max(0, newX), y: Math.max(0, newY) });
  }, [dragging, selectedId, dragOffset, updateElement]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleExport = async (format: 'png' | 'jpg') => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      if (!canvasRef.current) return;
      const canvas = await html2canvas(canvasRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: flyerState.bgColor,
        width: flyerState.width * SCALE,
        height: flyerState.height * SCALE,
      });
      const link = document.createElement('a');
      link.download = `flyer.${format}`;
      link.href = canvas.toDataURL(format === 'jpg' ? 'image/jpeg' : 'image/png', 0.95);
      link.click();
    } catch {
      alert('Export failed. Please try again.');
    }
  };

  const captureFlyer = async (): Promise<string | null> => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      if (!canvasRef.current) return null;
      const canvas = await html2canvas(canvasRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: flyerState.bgColor,
        width: flyerState.width * SCALE,
        height: flyerState.height * SCALE,
      });
      return canvas.toDataURL('image/png', 0.95);
    } catch {
      return null;
    }
  };

  // Expose captureFlyer to parent via callback when requested
  useEffect(() => {
    if (onCaptureFlyer) {
      (window as unknown as Record<string, unknown>).__flyerCapture = captureFlyer;
    }
  });

  const sortedElements = [...flyerState.elements].sort((a, b) => a.zIndex - b.zIndex);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
      {/* Left Toolbar */}
      <div className="xl:col-span-1 space-y-4">
        {/* Add Elements */}
        <div className="card-base p-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Add Elements</h3>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={addTextElement} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150">
              <Type size={18} className="text-primary" />
              <span className="text-xs font-medium text-foreground">Text</span>
            </button>
            <button onClick={() => addShapeElement('rect')} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150">
              <Square size={18} className="text-primary" />
              <span className="text-xs font-medium text-foreground">Rectangle</span>
            </button>
            <button onClick={() => addShapeElement('circle')} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150">
              <Circle size={18} className="text-primary" />
              <span className="text-xs font-medium text-foreground">Circle</span>
            </button>
            <button onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150">
              <ImageIcon size={18} className="text-primary" />
              <span className="text-xs font-medium text-foreground">Image</span>
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        </div>

        {/* Canvas Settings */}
        <div className="card-base p-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Canvas</h3>
          <div className="space-y-3">
            <div>
              <label className="label-base text-xs">Background Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={flyerState.bgColor}
                  onChange={(e) => setFlyerState((prev) => ({ ...prev, bgColor: e.target.value }))}
                  className="w-8 h-8 rounded cursor-pointer border border-border"
                />
                <span className="text-xs text-muted-foreground font-mono">{flyerState.bgColor}</span>
              </div>
            </div>
            <div>
              <label className="label-base text-xs">Size</label>
              <p className="text-xs text-muted-foreground">{flyerState.width} × {flyerState.height}px</p>
            </div>
          </div>
        </div>

        {/* Templates */}
        <div className="card-base p-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Templates</h3>
          <div className="space-y-1.5">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => onTemplateSelect(tpl.id)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: tpl.bgColor }} />
                <span className="text-xs text-foreground">{tpl.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Export */}
        <div className="card-base p-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Export</h3>
          <div className="space-y-2">
            <button onClick={() => handleExport('png')} className="btn-primary w-full justify-center text-xs py-2">
              <Download size={13} />
              Export as PNG
            </button>
            <button onClick={() => handleExport('jpg')} className="btn-secondary w-full justify-center text-xs py-2">
              <Download size={13} />
              Export as JPG
            </button>
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="xl:col-span-2 flex flex-col items-center">
        <div className="card-base p-4 w-full">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-muted-foreground">{flyerState.templateName}</span>
            <span className="text-xs text-muted-foreground">{flyerState.width} × {flyerState.height}px</span>
          </div>
          <div className="overflow-auto flex justify-center">
            <div
              ref={canvasRef}
              className="relative select-none"
              style={{
                width: flyerState.width * SCALE,
                height: flyerState.height * SCALE,
                backgroundColor: flyerState.bgColor,
                cursor: dragging ? 'grabbing' : 'default',
                boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
              }}
              onClick={() => setSelectedId(null)}
            >
              {sortedElements.map((el) => (
                <CanvasElementRenderer
                  key={el.id}
                  el={el}
                  scale={SCALE}
                  isSelected={selectedId === el.id}
                  onMouseDown={(e) => handleMouseDown(e, el.id)}
                  onUpdate={(updates) => updateElement(el.id, updates)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Properties Panel */}
      <div className="xl:col-span-1 space-y-4">
        {selectedEl ? (
          <ElementPropertiesPanel
            el={selectedEl}
            onUpdate={(updates) => updateElement(selectedEl.id, updates)}
            onDelete={() => deleteElement(selectedEl.id)}
            onDuplicate={() => duplicateElement(selectedEl.id)}
            totalElements={flyerState.elements.length}
          />
        ) : (
          <div className="card-base p-5 flex flex-col items-center justify-center text-center min-h-[200px]">
            <Layers size={28} className="text-muted-foreground mb-2" />
            <p className="text-sm font-medium text-foreground">No element selected</p>
            <p className="text-xs text-muted-foreground mt-1">Click an element on the canvas to edit its properties</p>
          </div>
        )}

        {/* Layers */}
        <div className="card-base p-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Layers ({flyerState.elements.length})</h3>
          {flyerState.elements.length === 0 ? (
            <p className="text-xs text-muted-foreground">No elements yet. Add elements using the toolbar.</p>
          ) : (
            <div className="space-y-1">
              {[...flyerState.elements].reverse().map((el) => (
                <button
                  key={el.id}
                  onClick={() => setSelectedId(el.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${
                    selectedId === el.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'
                  }`}
                >
                  {el.type === 'text' && <Type size={12} />}
                  {el.type === 'shape' && <Square size={12} />}
                  {el.type === 'image' && <ImageIcon size={12} />}
                  <span className="text-xs truncate flex-1">
                    {el.type === 'text' ? (el.content?.slice(0, 20) || 'Text') :
                     el.type === 'shape' ? `${el.shapeType || 'Shape'}` :
                     el.imageAlt?.slice(0, 20) || 'Image'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Canvas Element Renderer ----
interface CanvasElementRendererProps {
  el: CanvasElement;
  scale: number;
  isSelected: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}

function CanvasElementRenderer({ el, scale, isSelected, onMouseDown, onUpdate }: CanvasElementRendererProps) {
  const [editing, setEditing] = useState(false);

  const style: React.CSSProperties = {
    position: 'absolute',
    left: el.x * scale,
    top: el.y * scale,
    width: el.width * scale,
    height: el.height * scale,
    zIndex: el.zIndex,
    cursor: 'grab',
    outline: isSelected ? '2px solid #6C47FF' : 'none',
    outlineOffset: '1px',
    userSelect: 'none',
  };

  if (el.type === 'text') {
    return (
      <div
        style={style}
        onMouseDown={onMouseDown}
        onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      >
        {editing ? (
          <textarea
            autoFocus
            value={el.content || ''}
            onChange={(e) => onUpdate({ content: e.target.value })}
            onBlur={() => setEditing(false)}
            style={{
              width: '100%',
              height: '100%',
              fontSize: (el.fontSize || 24) * scale,
              fontWeight: el.fontWeight || '400',
              color: el.color || '#000000',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              textAlign: el.textAlign || 'left',
              lineHeight: 1.2,
              padding: 0,
              cursor: 'text',
              fontFamily: 'inherit',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              fontSize: (el.fontSize || 24) * scale,
              fontWeight: el.fontWeight || '400',
              color: el.color || '#000000',
              textAlign: el.textAlign || 'left',
              lineHeight: 1.2,
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {el.content}
          </div>
        )}
      </div>
    );
  }

  if (el.type === 'shape') {
    const isCircle = el.shapeType === 'circle';
    return (
      <div
        style={{
          ...style,
          backgroundColor: el.bgColor || '#6C47FF',
          borderRadius: isCircle ? '50%' : '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseDown={onMouseDown}
      >
        {el.content && (
          <span style={{
            fontSize: (el.fontSize || 16) * scale,
            fontWeight: el.fontWeight || '600',
            color: el.color || '#FFFFFF',
            textAlign: 'center',
            padding: '0 8px',
          }}>
            {el.content}
          </span>
        )}
      </div>
    );
  }

  if (el.type === 'image' && el.imageUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={el.imageUrl}
        alt={el.imageAlt || 'Flyer element'}
        style={{ ...style, objectFit: 'cover', display: 'block' }}
        onMouseDown={onMouseDown}
        draggable={false}
      />
    );
  }

  return null;
}

// ---- Element Properties Panel ----
interface ElementPropertiesPanelProps {
  el: CanvasElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  totalElements: number;
}

function ElementPropertiesPanel({ el, onUpdate, onDelete, onDuplicate }: ElementPropertiesPanelProps) {
  return (
    <div className="card-base p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Properties</h3>
        <div className="flex items-center gap-1">
          <button onClick={onDuplicate} className="p-1.5 rounded hover:bg-muted transition-colors" title="Duplicate">
            <Copy size={13} className="text-muted-foreground" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-negative/10 transition-colors" title="Delete">
            <Trash2 size={13} className="text-negative" />
          </button>
        </div>
      </div>

      {/* Position & Size */}
      <div>
        <label className="label-base text-xs">Position</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-[10px] text-muted-foreground">X</span>
            <input type="number" value={Math.round(el.x)} onChange={(e) => onUpdate({ x: Number(e.target.value) })} className="input-base text-xs py-1.5" />
          </div>
          <div>
            <span className="text-[10px] text-muted-foreground">Y</span>
            <input type="number" value={Math.round(el.y)} onChange={(e) => onUpdate({ y: Number(e.target.value) })} className="input-base text-xs py-1.5" />
          </div>
        </div>
      </div>

      <div>
        <label className="label-base text-xs">Size</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-[10px] text-muted-foreground">W</span>
            <input type="number" value={Math.round(el.width)} onChange={(e) => onUpdate({ width: Number(e.target.value) })} className="input-base text-xs py-1.5" />
          </div>
          <div>
            <span className="text-[10px] text-muted-foreground">H</span>
            <input type="number" value={Math.round(el.height)} onChange={(e) => onUpdate({ height: Number(e.target.value) })} className="input-base text-xs py-1.5" />
          </div>
        </div>
      </div>

      {/* Z-Index */}
      <div>
        <label className="label-base text-xs">Layer Order</label>
        <div className="flex items-center gap-2">
          <button onClick={() => onUpdate({ zIndex: Math.max(0, el.zIndex - 1) })} className="p-1.5 rounded border border-border hover:bg-muted">
            <ChevronDown size={13} />
          </button>
          <span className="text-xs text-foreground flex-1 text-center">{el.zIndex}</span>
          <button onClick={() => onUpdate({ zIndex: el.zIndex + 1 })} className="p-1.5 rounded border border-border hover:bg-muted">
            <ChevronUp size={13} />
          </button>
        </div>
      </div>

      {/* Text Properties */}
      {(el.type === 'text' || el.type === 'shape') && (
        <>
          {el.type === 'text' && (
            <div>
              <label className="label-base text-xs">Text Content</label>
              <textarea
                value={el.content || ''}
                onChange={(e) => onUpdate({ content: e.target.value })}
                rows={2}
                className="input-base text-xs resize-none"
              />
            </div>
          )}
          {el.type === 'shape' && (
            <div>
              <label className="label-base text-xs">Button Label</label>
              <input
                type="text"
                value={el.content || ''}
                onChange={(e) => onUpdate({ content: e.target.value })}
                className="input-base text-xs"
                placeholder="Optional text"
              />
            </div>
          )}

          <div>
            <label className="label-base text-xs">Font Size</label>
            <div className="flex items-center gap-2">
              <button onClick={() => onUpdate({ fontSize: Math.max(8, (el.fontSize || 16) - 2) })} className="p-1.5 rounded border border-border hover:bg-muted">
                <Minus size={12} />
              </button>
              <span className="text-xs text-foreground flex-1 text-center">{el.fontSize || 16}px</span>
              <button onClick={() => onUpdate({ fontSize: (el.fontSize || 16) + 2 })} className="p-1.5 rounded border border-border hover:bg-muted">
                <Plus size={12} />
              </button>
            </div>
          </div>

          <div>
            <label className="label-base text-xs">Font Weight</label>
            <select
              value={el.fontWeight || '400'}
              onChange={(e) => onUpdate({ fontWeight: e.target.value })}
              className="input-base text-xs"
            >
              <option value="400">Regular</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
              <option value="900">Black</option>
            </select>
          </div>

          {el.type === 'text' && (
            <div>
              <label className="label-base text-xs">Text Align</label>
              <div className="flex items-center gap-1">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    onClick={() => onUpdate({ textAlign: align })}
                    className={`flex-1 p-2 rounded border transition-colors ${el.textAlign === align ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}
                  >
                    {align === 'left' && <AlignLeft size={13} className="mx-auto" />}
                    {align === 'center' && <AlignCenter size={13} className="mx-auto" />}
                    {align === 'right' && <AlignRight size={13} className="mx-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label-base text-xs">Text Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={el.color || '#000000'}
                onChange={(e) => onUpdate({ color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border border-border"
              />
              <span className="text-xs text-muted-foreground font-mono">{el.color || '#000000'}</span>
            </div>
          </div>
        </>
      )}

      {/* Shape Background Color */}
      {el.type === 'shape' && (
        <div>
          <label className="label-base text-xs">Fill Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={el.bgColor || '#6C47FF'}
              onChange={(e) => onUpdate({ bgColor: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer border border-border"
            />
            <span className="text-xs text-muted-foreground font-mono">{el.bgColor || '#6C47FF'}</span>
          </div>
        </div>
      )}

      {/* Reset */}
      <button
        onClick={() => onUpdate({ x: 60, y: 60 })}
        className="btn-ghost w-full justify-center text-xs"
      >
        <RotateCcw size={12} />
        Reset Position
      </button>
    </div>
  );
}
